import React from "react"
import { Link, graphql } from "gatsby"
import Layout from "../components/layout"
import SEO from "../components/seo"
import PostBlock from "../components/PostBlock"
import getPostSchema from "../postSchema"
import styles from "../styles/post.module.scss"
import twitter from "../images/twitter-icon.svg"
import facebook from "../images/facebook-icon.svg"
import linkedin from "../images/linkedin-icon.svg"
import dayjs from "dayjs"
import Img from "gatsby-image"
import contentParser from "gatsby-wpgraphql-inline-images"

export const query = graphql`
  query GET_POST($postId: ID!, $categoryId: Int!, $databaseId: [ID]) {
    wpgraphql {
      thePost: post(idType: DATABASE_ID, id: $postId) {
        uri
        id
        seo {
          metaDesc
          title
          opengraphTitle
          opengraphDescription
          twitterDescription
          twitterTitle
        }
        content(format: RENDERED)
        title
        slug
        author {
          node {
            name
            slug
            avatar {
              url
            }
          }
        }
        date
        categories {
          edges {
            node {
              name
              slug
            }
          }
        }
        dateGmt
        featuredImage {
          node {
            altText
            sourceUrl
            imageFile {
              childImageSharp {
                fluid(maxWidth: 1300, quality: 80, webpQuality: 80) {
                  ...GatsbyImageSharpFluid_withWebp_noBase64
                }

                # og image
                fixed(width: 1200) {
                  base64
                  width
                  height
                  src
                  srcSet
                }
              }
            }
          }
        }
      }

      relatedPosts: posts(
        where: { categoryId: $categoryId, notIn: $databaseId }
        first: 3
      ) {
        nodes {
          id
          title
          uri
          slug
          excerpt(format: RENDERED)
          author {
            node {
              name
              slug
              avatar(size: 34) {
                url
              }
            }
          }
          featuredImage {
            node {
              altText
              sourceUrl
              imageFile {
                childImageSharp {
                  fluid(maxWidth: 490, webpQuality: 85) {
                    ...GatsbyImageSharpFluid_withWebp_noBase64
                  }
                }
              }
            }
          }
          categories {
            edges {
              node {
                name
                slug
              }
            }
          }
        }
      }
    }

    jamieProfile: file(relativePath: { eq: "jdavidson.jpg" }) {
      childImageSharp {
        fixed(width: 38, height: 38) {
          ...GatsbyImageSharpFixed_withWebp_noBase64
        }
      }
    }

    kurtProfile: file(relativePath: { eq: "kurtbirk.jpg" }) {
      childImageSharp {
        fixed(width: 38, height: 38) {
          ...GatsbyImageSharpFixed_withWebp_noBase64
        }
      }
    }
  }
`

const Post = ({ data, location }) => {
  const postData = data.wpgraphql.thePost
  const seo = postData.seo

  const pluginOptions = {
    wordPressUrl: `${process.env.GATSBY_WP_DOMAIN}/`,
    uploadsUrl: `${process.env.GATSBY_WP_DOMAIN}/wp-content/uploads/`,
  }

  const baseUrl = "https://www.conferencecalling.com"

  const handleSocialLink = e => {
    let socialLink = ""

    switch (e.target.id) {
      case "linkedin":
        socialLink = `https://www.linkedin.com/sharing/share-offsite/?url=${baseUrl}${location.pathname}`
        break
      case "twitter":
        socialLink = `https://twitter.com/intent/tweet?text=${postData.seo.title}.&url=${baseUrl}${location.pathname}`
        break
      case "facebook":
        socialLink = `https://www.facebook.com/sharer/sharer.php?u=${baseUrl}${location.pathname}`
        break
      default:
      // code block
    }

    typeof window !== "undefined" && window.open(socialLink)
  }

  // related posts
  const relatedPostBlocks = data.wpgraphql.relatedPosts.nodes

  const relatedCategories = postData.categories.edges.map((category, index) => (
    <Link key={index} to={`/blog/category/${category.node.slug}`}>
      {category.node.name.toUpperCase()}
    </Link>
  ))

  const result = []
    .concat(
      ...relatedCategories.map(e => [
        <h4 className={styles.categoryBreak}>/</h4>,
        e,
      ])
    )
    .slice(1)

  const bgImageStyle = {
    position: "absolute",
    left: 0,
    top: 0,
    width: "100%",
    height: "100%",
    backgroundRepeat: "no-repeat",
    backgroundSize: postData.featuredImage ? `cover` : null,
    backgroundPosition: postData.featuredImage ? "0% 50%" : null,
  }

  return (
    <Layout colorBlock={true} blogBadge={true}>
      <SEO
        {...seo}
        ogImage={
          postData.featuredImage
            ? postData.featuredImage.node.imageFile.childImageSharp.fixed.src
            : null
        }
        schema={getPostSchema(postData.slug)}
      />
      <div
        className={`${styles.hero} ${
          !postData.featuredImage ? styles.noImage : ""
        }`}
      >
        {postData.featuredImage && (
          <Img
            fluid={postData.featuredImage.node.imageFile.childImageSharp.fluid}
            style={bgImageStyle}
            alt={postData.featuredImage.node.altText}
            loading="eager"
            fadeIn={false}
          />
        )}
        <div className={styles.postHeader}>
          <div className={styles.postHeaderCategories}>{result}</div>
          <h1
            className={styles.title}
            dangerouslySetInnerHTML={{ __html: postData.title }}
          />
        </div>
      </div>

      <div className={styles.postContent}>
        <div className={styles.detailsHeader}>
          <div className={styles.authorDataContainer}>
            <div className={styles.avatar}>
              {postData.author.node.slug === "jdavidson" && (
                <Img
                  fixed={data.jamieProfile.childImageSharp.fixed}
                  alt="avatar"
                  loading="eager"
                  fadeIn={false}
                  style={{ width: "38px", height: "38px" }}
                  imgStyle={{
                    width: "38px",
                    height: "38px",
                  }}
                />
              )}

              {postData.author.node.slug === "kurtbirk" && (
                <Img
                  fixed={data.kurtProfile.childImageSharp.fixed}
                  alt="avatar"
                  loading="eager"
                  fadeIn={false}
                  style={{ width: "38px", height: "38px" }}
                  imgStyle={{
                    width: "38px",
                    height: "38px",
                  }}
                />
              )}
            </div>

            <div>
              <Link
                to={`/blog/author/${postData.author.node.slug}`}
                className={styles.author}
              >{`By ${postData.author.node.name} `}</Link>
              <span className={styles.dotSeparator}>{` · `}</span>
              <span className={styles.date}>{`${dayjs(postData.date).format(
                "MMMM D, YYYY"
              )}`}</span>
            </div>
          </div>
          <div className={styles.socialContainer}>
            <img
              onClick={handleSocialLink}
              id="linkedin"
              className={styles.socialImage}
              src={linkedin}
              alt="linkedin icon"
              width="21"
              height="21"
              loading="eager"
            />
            <img
              onClick={handleSocialLink}
              id="twitter"
              className={styles.socialImage}
              src={twitter}
              alt="twitter icon"
              width="20"
              height="20"
              loading="eager"
            />
            <img
              onClick={handleSocialLink}
              id="facebook"
              className={styles.socialImage}
              src={facebook}
              alt="facebook icon"
              width="20"
              height="20"
              loading="eager"
            />
          </div>
        </div>
        <div
          className={styles.postBody}
        >
          {contentParser({ content: postData.content }, pluginOptions)}
        </div>
      </div>

      <div className={styles.relatedContainer}>
        <div className={styles.relatedHeader}>
          <div className={styles.headerCategories}>{result}</div>

          <h2>Related Articles</h2>
        </div>

        <div
          className={styles.relatedGridContainer}
          style={{
            maxWidth: relatedPostBlocks.length < 3 ? "1000px" : "1250px",
          }}
        >
          {relatedPostBlocks.map((post, index) => {
            if (index > 2) return

            return (
              <PostBlock
                key={post.id}
                slug={post.slug}
                title={post.title}
                category={post.categories.edges[0].node.name}
                excerpt={post.excerpt}
                avatarSlug={post.author.node.slug}
                author={`${post.author.node.name}`}
                featuredImage={
                  post.featuredImage && (
                    <Img
                      fluid={
                        post.featuredImage.node.imageFile.childImageSharp.fluid
                      }
                      alt={post.featuredImage.node.altText}
                    />
                  )
                }
              />
            )
          })}
        </div>
      </div>
    </Layout>
  )
}

export default Post
